import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getUserInfo, updateCurrentUserInfo } from '../../services/auth';
import { useSnackbar } from 'notistack';
import { logout } from '../../slices/auth/authSlice';
import {
  ArrowRight,
  ArrowUpCircle,
  CreditCard,
  HelpCircle,
  LogOut,
  Settings,
  Sliders,
  User,
} from 'react-feather';
import { hapticsImpactLight } from '../../utils/haptics';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import OraSwitch from '../../components/CustomMUI/Switch';
import { TriangleRight } from '../../assets/svgs/TriangleRight';
import Page from '../../components/Page';
import { useUpdateTheme } from '../../hooks/useUpdateTheme';
import posthog from 'posthog-js';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import moment from 'moment-timezone';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { ThemeMode } from '../../types/User';
import useWindowWidth from '../../hooks/useWindowWidth';
import Billing from '../../components/Account/Billing';
import { BlockTimeSetting } from '../../types/Study';
import FlashcardSettings from '../../components/Study/Flashcards/FlashcardSettings';
// import { examsSelectors } from '../../slices/exams/examsSlice';
import { Capacitor } from '@capacitor/core';
import { selectSubscription } from '../../slices/subscriptions/subscriptionsSlice';
import { SubscriptionStatus } from '../../types/Subscription';
import EmailRemindersSettings from '../../components/Account/EmailRemindersSettings';
import AccountProfile from '../../components/Account/AccountProfile';
import {
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
  RedditLogo,
} from '@phosphor-icons/react';
import BlueskyLogo from '../../assets/svgs/bluesky.svg';

function decodeHTMLEntities(str: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

enum AccountPage {
  Profile = 'profile',
  StudySettings = 'study-settings',
  Preferences = 'preferences',
  Billing = 'billing',
}

const Account: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const subscription = useSelector(selectSubscription);

  const [activePage, setActivePage] = useState(AccountPage.Profile);
  const [showAccountMenu, setShowAccountMenu] = useState(true);
  const [userTheme, setUserTheme] = useState(currentUser?.theme);
  const [demoMode, setDemoMode] = useState(currentUser?.demoMode || false);
  // const [screensaverPreference, setScreensaverPreference] = useState(
  //   currentUser?.screensaverPreference || 'none'
  // );
  const [blockTimeSettingPreference, setBlockTimeSettingPreference] = useState(
    currentUser?.preferredBlockTimeSetting || 'default'
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(
    currentUser?.timezone || null
  );
  const [localBlockSize, setLocalBlockSize] = useState(
    currentUser?.blockSize || 40
  );
  const [isBlockSizeChanged, setIsBlockSizeChanged] = useState(false);
  const [
    preferredLearningObjectiveLocation,
    setPreferredLearningObjectiveLocation,
  ] = useState(currentUser?.preferredLearningObjectiveLocation || 'bottom');

  const windowWidth = useWindowWidth();

  const hasMounted = useRef(false);

  const { updateTheme } = useUpdateTheme();

  useEffect(() => {
    if (hasMounted.current) {
      if (selectedTimezone) {
        handleUpdateTimezone();
      }
    } else {
      hasMounted.current = true;
    }
  }, [selectedTimezone]);

  useEffect(() => {
    getUserInfo();
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subpageKey = urlParams.get('sub');
    const openMenu = urlParams.get('open-menu');
    if (openMenu) {
      setShowAccountMenu(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (
      subpageKey &&
      Object.values(AccountPage).includes(subpageKey as AccountPage)
    ) {
      setActivePage(subpageKey as AccountPage);
    }
  }, [navigate, window.location.search]);

  const handleUpdateTimezone = async () => {
    if (selectedTimezone) {
      const updateUser = await updateCurrentUserInfo({
        timezone: selectedTimezone,
      });
      if (updateUser) {
        enqueueSnackbar('Timezone updated.', {
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(
          'Error updating timezone. Please reload the page and try again.'
        );
      }
    }
  };

  const updateDemoMode = async (demoMode: boolean) => {
    await updateCurrentUserInfo({ demoMode: demoMode });
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/auth');
  };

  const handleThemeChange = (theme: ThemeMode) => {
    if (theme !== null) {
      setUserTheme(theme);
      posthog?.capture('user_changed_theme', { theme: theme });
      updateTheme(theme);
    }
  };

  const handleDemoModeChange = (demoMode: boolean) => {
    setDemoMode(demoMode);
    updateDemoMode(demoMode);
  };

  // const handleScreensaverPreferenceChange = async (
  //   screensaverPreference: ScreensaverOption
  // ) => {
  //   setScreensaverPreference(screensaverPreference);
  //   const updateUser = await updateCurrentUserInfo({
  //     screensaverPreference: screensaverPreference,
  //   });
  //   if (updateUser) {
  //     dispatch(setUserInfo(updateUser));
  //     enqueueSnackbar('Screensaver preference updated.', {
  //       autoHideDuration: 3000,
  //     });
  //   }
  // };

  const timezoneOptions = moment.tz.names().map((zone) => {
    let offset = moment.tz(zone).format('Z');
    offset = offset.replace('+', '&#xff0b;').replace('-', '&#xff0d;');
    const parts = zone.split('/');
    const region = parts[0];
    const locationParts = parts.slice(1).map((p) => p.replace('_', ' '));
    const locationStr = locationParts.join(' - ');

    return {
      label: `GMT${offset} - ${region}${locationStr ? ` - ${locationStr}` : ''}`,
      value: zone,
      monospacedOffset: offset,
      region,
      city: locationStr,
    };
  });

  const handleBlockTimeSettingChange = async (
    blockTimeSetting: BlockTimeSetting
  ) => {
    setBlockTimeSettingPreference(blockTimeSetting);
    const updateUser = await updateCurrentUserInfo({
      preferredBlockTimeSetting: blockTimeSetting,
    });
    if (updateUser) {
      enqueueSnackbar('QBank time accommodation updated.', {
        autoHideDuration: 3000,
      });
    }
  };

  const handleAccountMenuClick = (page: AccountPage) => {
    hapticsImpactLight();
    setActivePage(page);
    if (windowWidth < 900) {
      setShowAccountMenu(false);
    }
  };

  const handleBlockSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const parsedValue = parseInt(value);

    // Only update if the parsed value is a valid number
    if (!isNaN(parsedValue)) {
      const blockSize = Math.max(1, Math.min(100, parsedValue));
      setLocalBlockSize(blockSize);
      setIsBlockSizeChanged(blockSize !== currentUser?.blockSize);
    }
  };

  const handleSaveBlockSize = async () => {
    const updateUser = await updateCurrentUserInfo({
      blockSize: localBlockSize,
    });
    if (updateUser) {
      enqueueSnackbar('Block size updated.', { autoHideDuration: 3000 });
      setIsBlockSizeChanged(false);
    }
  };

  const handlePreferredLearningObjectiveLocationChange = async (
    _event: React.MouseEvent<HTMLElement>,
    newPreferredLearningObjectiveLocation: string
  ) => {
    setPreferredLearningObjectiveLocation(
      newPreferredLearningObjectiveLocation
    );
    const updateUser = await updateCurrentUserInfo({
      preferredLearningObjectiveLocation: newPreferredLearningObjectiveLocation,
    });
    if (updateUser) {
      enqueueSnackbar('Preferred learning objective location updated.', {
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <Page type="account">
      <div className="account">
        <div className="account_main">
          <div className="account_main_menu">
            <div className={`account-menu ${showAccountMenu ? 'is-open' : ''}`}>
              <div className="account-menu_header">
                <h2>Account</h2>
              </div>
              {subscription?.status === SubscriptionStatus.FREE &&
                !(Capacitor.getPlatform() === 'android') &&
                !subscription?.grandfathered && (
                  <button
                    className={`button`}
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/pricing');
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <ArrowUpCircle />
                      <span>Upgrade</span>
                    </div>
                    <ArrowRight />
                  </button>
                )}
              <button
                className={`button ${
                  activePage === AccountPage.Profile ? 'is-active' : ''
                }`}
                onClick={() => handleAccountMenuClick(AccountPage.Profile)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <User />
                  <span>Profile</span>
                </div>
                <ArrowRight />
              </button>
              <button
                className={`button ${
                  activePage === AccountPage.StudySettings ? 'is-active' : ''
                }`}
                onClick={() =>
                  handleAccountMenuClick(AccountPage.StudySettings)
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Settings />
                  <span>Study Settings</span>
                </div>
                <ArrowRight />
              </button>
              <button
                className={`button ${
                  activePage === AccountPage.Preferences ? 'is-active' : ''
                }`}
                onClick={() => handleAccountMenuClick(AccountPage.Preferences)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Sliders />
                  <span>Preferences</span>
                </div>
                <ArrowRight />
              </button>
              <button
                className={`button ${
                  activePage === AccountPage.Billing ? 'is-active' : ''
                }`}
                onClick={() => handleAccountMenuClick(AccountPage.Billing)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <CreditCard />
                  <span>Billing</span>
                </div>
                <ArrowRight />
              </button>
              <button
                className={`button button--help`}
                onClick={() => {
                  navigate('/help');
                  hapticsImpactLight();
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <HelpCircle />
                  <span>Help</span>
                </div>
                <ArrowRight />
              </button>
              <button
                className={`button button--logout`}
                onClick={() => {
                  hapticsImpactLight();
                  handleSignOut();
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <LogOut />
                  <span>Log out</span>
                </div>
              </button>
              <div className="account-menu_footer">
                <h3>Connect on Socials</h3>
                <div className="media-links">
                  <a
                    className="media"
                    target="_blank"
                    href="https://www.reddit.com/r/medschoolora/"
                  >
                    <RedditLogo />
                  </a>
                  <a
                    className="media"
                    target="_blank"
                    href="https://www.instagram.com/oraai_hq"
                  >
                    <InstagramLogo />
                  </a>
                  <a
                    className="media"
                    target="_blank"
                    href="https://www.facebook.com/profile.php?id=61577778571187"
                  >
                    <FacebookLogo />
                  </a>
                  <a
                    className="media"
                    target="_blank"
                    href="https://bsky.app/profile/oraai.com"
                  >
                    <img src={BlueskyLogo} alt="Bluesky" />
                  </a>
                  <a
                    className="media"
                    target="_blank"
                    href="https://www.linkedin.com/company/synaptiq-learning/"
                  >
                    <LinkedinLogo />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="account_main_page">
            {activePage === AccountPage.Profile && <AccountProfile />}
            {activePage === AccountPage.StudySettings && (
              <>
                <h1>Study Settings</h1>
                <div className="block block--settings">
                  <div className="block_body">
                    <h3>QBank Settings</h3>
                    <InputLabel>Default Block Size</InputLabel>
                    <TextField
                      className="m-t-0-50"
                      id="default-block-size"
                      variant="outlined"
                      value={localBlockSize}
                      onChange={handleBlockSizeChange}
                      disabled={currentUser?.cohort === 'rct'}
                      type="number"
                      fullWidth
                      inputProps={{
                        min: 1,
                        max: 100,
                      }}
                    />
                    <div
                      className="helper"
                      style={{
                        marginRight: 'auto',
                        marginLeft: '0',
                        marginTop: '.25rem',
                      }}
                    >
                      The number of questions in a QBank block.
                    </div>
                    {isBlockSizeChanged && (
                      <div className="m-t-1-50">
                        <button
                          className="button button--secondary"
                          onClick={() => {
                            setLocalBlockSize(currentUser?.blockSize || 40);
                            setIsBlockSizeChanged(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="button"
                          onClick={() => {
                            handleSaveBlockSize();
                            hapticsImpactLight();
                          }}
                        >
                          Save Block Size
                        </button>
                      </div>
                    )}
                    <InputLabel
                      id="block-time-setting-preference"
                      className="m-t-1-50 m-b-0-50"
                    >
                      QBank Time Accommodation
                    </InputLabel>
                    <Select
                      labelId="block-time-setting-preference"
                      id="block-time-setting-preference"
                      value={blockTimeSettingPreference}
                      disabled={currentUser?.cohort === 'rct'}
                      onChange={(e) => {
                        handleBlockTimeSettingChange(
                          e.target.value as BlockTimeSetting
                        );
                      }}
                      style={{ width: '100%', marginTop: '0' }}
                    >
                      <MenuItem value={BlockTimeSetting.default}>
                        <TriangleRight />
                        Default
                      </MenuItem>
                      <MenuItem value={BlockTimeSetting.quarter}>
                        <TriangleRight />
                        25% Additional Testing Time (Time and 1/4)
                      </MenuItem>
                      <MenuItem value={BlockTimeSetting.half}>
                        <TriangleRight />
                        50% Additional Testing Time (Time and 1/2)
                      </MenuItem>
                      <MenuItem value={BlockTimeSetting.double}>
                        <TriangleRight />
                        100% Additional Testing Time (Double Time)
                      </MenuItem>
                    </Select>
                    <div
                      className="helper"
                      style={{
                        marginRight: 'auto',
                        marginLeft: '0',
                        marginTop: '.25rem',
                      }}
                    >
                      Only affects timed QBank blocks.
                    </div>
                    <div className="control m-t-1-50 control--preferred-learning-objective-location">
                      <InputLabel className="m-b-0-50">
                        Learning Objective Placement
                      </InputLabel>
                      <ToggleButtonGroup
                        value={preferredLearningObjectiveLocation}
                        exclusive
                        onChange={
                          handlePreferredLearningObjectiveLocationChange
                        }
                        aria-label="preferred learning objective location"
                        sx={{ width: '100%' }}
                      >
                        <ToggleButton value="top" aria-label="top">
                          Top
                        </ToggleButton>
                        <ToggleButton value="bottom" aria-label="bottom">
                          Bottom
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <div
                        className="helper"
                        style={{
                          marginRight: 'auto',
                          marginLeft: '0',
                          marginTop: '.25rem',
                        }}
                      >
                        Determines if the Learning Objective is displayed above
                        or below the QBank explanation.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="block block--settings block--settings--flashcards">
                  <div className="block_body">
                    <h3>Flashcard Settings</h3>
                    <FlashcardSettings />
                  </div>
                </div>
              </>
            )}
            {activePage === AccountPage.Preferences && (
              <>
                <h1>Preferences</h1>
                <div className="block block--settings">
                  <div className="block_body">
                    <h3>Timezone</h3>
                    <Autocomplete
                      options={timezoneOptions}
                      getOptionLabel={(option) =>
                        decodeHTMLEntities(option.label)
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span
                            style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                            dangerouslySetInnerHTML={{
                              __html: `GMT${option.monospacedOffset}`,
                            }}
                          />
                          &nbsp;&nbsp;
                          {option.region}
                          {option.city ? ` - ${option.city}` : ''}
                        </li>
                      )}
                      value={
                        timezoneOptions.find(
                          (o) => o.value === selectedTimezone
                        ) || undefined
                      }
                      onChange={(_event, newValue) => {
                        setSelectedTimezone(newValue?.value || null);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" fullWidth />
                      )}
                      disableClearable
                    />
                  </div>
                  <div className="block_footer">
                    <div
                      className="helper"
                      style={{ marginRight: 'auto', marginLeft: '0' }}
                    >
                      Determines when your daily session resets.
                    </div>
                  </div>
                </div>
                <EmailRemindersSettings />
                <div className="block block--settings">
                  <div className="block_body">
                    <h3>Theme</h3>
                    <div
                      className="field"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        justifyContent: 'space-between',
                      }}
                    >
                      <ToggleButtonGroup
                        value={userTheme}
                        exclusive
                        onChange={(_e, newValue) => handleThemeChange(newValue)}
                        aria-label="Theme Options"
                        sx={{ width: '100%' }}
                      >
                        <ToggleButton value="system" aria-label="System">
                          System
                        </ToggleButton>
                        <ToggleButton value="light" aria-label="Light">
                          Light
                        </ToggleButton>
                        <ToggleButton value="dark" aria-label="Dark">
                          Dark
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    {currentUser?.role === 'admin' && (
                      <div
                        className="field m-t-1"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          justifyContent: 'space-between',
                        }}
                      >
                        <InputLabel id="timezone-label">Demo Mode</InputLabel>
                        <OraSwitch
                          checked={demoMode}
                          onChange={(e) =>
                            handleDemoModeChange(e.target.checked)
                          }
                          onClick={() => {
                            hapticsImpactLight();
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* {windowWidth > 900 && (
                  <div className="block block--settings">
                    <div className="block_body">
                      <h3>Screensaver</h3>
                      <Select
                        labelId="screensaver-preference"
                        id="screensaver-preference"
                        value={screensaverPreference}
                        onChange={(e) => {
                          handleScreensaverPreferenceChange(
                            e.target.value as ScreensaverOption
                          );
                        }}
                        style={
                          windowWidth > 560
                            ? { width: '50%', marginTop: '0' }
                            : { width: '100%', marginTop: '0' }
                        }
                      >
                        <MenuItem value="none">
                          <TriangleRight />
                          Off
                        </MenuItem>
                        <MenuItem value="thirtySeconds">
                          <TriangleRight />
                          30 Seconds
                        </MenuItem>
                        <MenuItem value="oneMinute">
                          <TriangleRight />
                          One Minute
                        </MenuItem>
                        <MenuItem value="twoMinutes">
                          <TriangleRight />
                          Two Minutes
                        </MenuItem>
                        <MenuItem value="fiveMinutes">
                          <TriangleRight />
                          Five Minutes
                        </MenuItem>
                        <MenuItem value="tenMinutes">
                          <TriangleRight />
                          10 Minutes
                        </MenuItem>
                      </Select>
                    </div>
                    <div className="block_footer">
                      <div
                        className="helper"
                        style={{ marginRight: 'auto', marginLeft: '0' }}
                      >
                        Shows Learning Objectives from today's session.
                      </div>
                    </div>
                  </div>
                )} */}
              </>
            )}
            {activePage === AccountPage.Billing && (
              <>
                <h1>Billing</h1>
                <Billing />
              </>
            )}
          </div>
          <div className="account_main_spacer"></div>
        </div>
      </div>
    </Page>
  );
};

export default Account;
